import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Badge,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
} from 'reactstrap';
import { toast } from 'react-toastify';
import moment from 'moment';
import ContentCopyIcon from 'mdi-react/ContentCopyIcon';
import EmailOutlineIcon from 'mdi-react/EmailOutlineIcon';
import CloseCircleOutlineIcon from 'mdi-react/CloseCircleOutlineIcon';
import ReplayIcon from 'mdi-react/ReplayIcon';
import CashRefundIcon from 'mdi-react/CashRefundIcon';
import Api from '../../../util/api';
import { partial } from '../../../util/translation/translation';
import { formatDateTime } from '../../../util/date-and-time';
import FormCard from '../../../shared/components/custom/FormCard';
import SquareIconButton from '../../../shared/components/custom/SquareIconButton';
import AlertModal, {
  AlertModalType,
} from '../../../shared/components/custom/AlertModal';
import PaymentOfferModal from './PaymentOfferModal';
import useOverLimitConfirm, {
  OVER_LIMIT_DECLINED,
} from '../useOverLimitConfirm';
import {
  formatMoney,
  paymentErrorMessage,
  refundableAmount,
  refundedAmountOf,
} from '../payments';

// How many expired/cancelled holds show before "Show all"
const HISTORY_PREVIEW = 5;

const RESENDABLE = ['offer', 'payment_link'];

const personName = (row) =>
  [row.firstname, row.lastname].filter(Boolean).join(' ') || row.email || '-';

/**
 * The money side of a paid event's participant tab:
 * - Awaiting payment: places held until paid (bookings in the app, waitlist
 *   offers, payment links, invites), with copy link / resend / cancel
 * - their history (expired, cancelled), with "offer again" / "send new link"
 * - payments that belong to no booking (e.g. someone who paid twice, or a
 *   booking that was removed), to refund
 * - on a cancelled event: refund every payment that has something left
 */
const EventPaymentsPanel = ({
  event,
  currency,
  pending,
  transactions,
  participants,
  deletedParticipants,
  onChanged,
  onRefund,
  setLoading,
}) => {
  const p = partial('EventPayments');
  const s = partial('shared');
  const [cancelTarget, setCancelTarget] = useState(null);
  const [again, setAgain] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [refundAll, setRefundAll] = useState(null);

  const money = (ore, rowCurrency) => formatMoney(ore, rowCurrency || currency);

  const holding = useMemo(
    () =>
      pending
        .filter((row) => row.status === 'holding')
        .sort((a, b) => new Date(a.expiresAt) - new Date(b.expiresAt)),
    [pending]
  );
  const history = useMemo(
    () =>
      pending
        .filter((row) => ['expired', 'cancelled'].includes(row.status))
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [pending]
  );

  // Completed payments of the event that no participant has: none of the
  // current participants paid with them, and they aren't still being turned
  // into a booking (a hold that's holding)
  const orphanPayments = useMemo(() => {
    const used = new Set(
      participants.map((item) => item.transactionId).filter(Boolean)
    );
    const inProgress = new Set(
      pending.filter((row) => row.status === 'holding').map((row) => row.id)
    );
    return transactions
      .filter(
        (transaction) =>
          ['completed', 'refunded'].includes(transaction.status) &&
          !used.has(transaction.id) &&
          !inProgress.has(transaction.metadata?.pendingBookingId)
      )
      .map((transaction) => ({
        transaction,
        removed: deletedParticipants.find(
          (item) => item.transactionId === transaction.id
        ),
      }));
  }, [transactions, participants, deletedParticipants, pending]);

  const refundable = useMemo(
    () => transactions.filter((transaction) => refundableAmount(transaction)),
    [transactions]
  );
  const refundableTotal = refundable.reduce(
    (total, transaction) => total + refundableAmount(transaction),
    0
  );

  const nameForTransaction = (transaction) => {
    const owner = [...participants, ...deletedParticipants].find(
      (item) => item.transactionId === transaction.id
    );
    return owner ? personName(owner) : transaction.userEmail;
  };

  const { sendWithOverride, confirmModal: overLimitModal } =
    useOverLimitConfirm();

  const run = async (action, successText) => {
    setLoading(true);
    try {
      // The admin chose not to go over the event's places: nothing happened
      if ((await action()) === OVER_LIMIT_DECLINED) return;
      if (successText) toast.success(successText);
      await onChanged();
    } catch (err) {
      toast.error(paymentErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const copyLink = async (row) => {
    try {
      await navigator.clipboard.writeText(row.payLink);
      toast.success(p('linkCopied'));
    } catch {
      toast.error(s('somethingWentWrong'));
    }
  };

  const resend = (row) =>
    run(
      () => Api.events.resendPendingBooking(row.id),
      p('resent', { name: personName(row) })
    );

  const cancelHold = async () => {
    const row = cancelTarget;
    setCancelTarget(null);
    await run(
      () => Api.events.cancelPendingBooking(row.id),
      p('holdCancelled', { name: personName(row) })
    );
  };

  // A new offer for the same waiter, or a new payment link for the same
  // person, at the price the admin confirms
  const sendAgain = async ({ price, bookingFee, expiresAt }) => {
    const row = again;
    setBusy(true);
    await run(
      async () => {
        // Over the event's places only once the admin says so
        const result = await sendWithOverride((ignoreLimits) =>
          row.kind === 'offer'
            ? Api.events.offerWaitlistPlace(row.waitingParticipantId, {
                price,
                bookingFee,
                ...(expiresAt && { expiresAt }),
                ...(ignoreLimits && { ignoreLimits }),
              })
            : Api.events.addParticipants(
                {
                  ...(ignoreLimits && { ignoreLimits }),
                  userId: row.userId || null,
                  companyId: null,
                  firstname: row.firstname,
                  lastname: row.lastname,
                  phoneNumber: row.phoneNumber,
                  email: row.email,
                  participantType: row.userId ? 'USER' : 'GUEST',
                  friendAmount: Number(row.friendAmount) || 0,
                  ...(Array.isArray(row.guests) && { guests: row.guests }),
                  additionalNotes: '',
                  payment: {
                    mode: 'link',
                    price,
                    bookingFee,
                    ...(expiresAt && { expiresAt }),
                  },
                },
                event.id
              )
        );
        if (result === OVER_LIMIT_DECLINED) return result;
        setAgain(null);
        return result;
      },
      p(row.kind === 'offer' ? 'offerSent' : 'linkSent', {
        name: personName(row),
      })
    );
    setBusy(false);
  };

  // One at a time, so each gets its own result
  const refundEverything = async () => {
    setRefundAll((state) => ({ ...state, running: true }));
    const failures = [];
    let refunded = 0;

    for (const transaction of refundable) {
      try {
        await Api.transactions.refundTransaction(transaction.id);
        refunded += 1;
      } catch (err) {
        failures.push({
          id: transaction.id,
          name: nameForTransaction(transaction),
          message: paymentErrorMessage(err),
        });
      }
    }
    setRefundAll({ running: false, done: true, refunded, failures });
    await onChanged();
  };

  const kindLabel = (row) => p(`kind-${row.kind}`);

  const renderHolding = () =>
    holding.map((row) => {
      const expired = row.expiresAt && moment(row.expiresAt).isBefore();
      return (
        <tr key={row.id}>
          <td className="table-stack__primary">
            {personName(row)}
            <small className="text-muted d-block">
              {[kindLabel(row), row.email].filter(Boolean).join(' · ')}
            </small>
          </td>
          <td data-label={p('places')}>{row.places}</td>
          <td data-label={p('amount')}>{money(row.amount, row.currency)}</td>
          <td data-label={p('timeLeft')}>
            <span title={row.expiresAt ? formatDateTime(row.expiresAt) : ''}>
              {expired
                ? p('expiredJustNow')
                : p('expires', { time: moment(row.expiresAt).fromNow() })}
            </span>
            {row.reminderSentAt && (
              <small className="text-muted d-block">
                {p('reminderSent', {
                  date: formatDateTime(row.reminderSentAt),
                })}
              </small>
            )}
          </td>
          <td className="table-stack__actions">
            <div className="icon-actions">
              {row.payLink && (
                <SquareIconButton
                  icon={<ContentCopyIcon />}
                  title={p('copyLink')}
                  id={`pending-copy-${row.id}`}
                  onClick={() => copyLink(row)}
                />
              )}
              {RESENDABLE.includes(row.kind) && (
                <SquareIconButton
                  icon={<EmailOutlineIcon />}
                  title={p('resend')}
                  id={`pending-resend-${row.id}`}
                  onClick={() => resend(row)}
                />
              )}
              <SquareIconButton
                icon={<CloseCircleOutlineIcon />}
                title={p('cancelHold')}
                tone="danger"
                id={`pending-cancel-${row.id}`}
                onClick={() => setCancelTarget(row)}
              />
            </div>
          </td>
        </tr>
      );
    });

  const canSendAgain = (row) =>
    (row.kind === 'offer' && row.waitingParticipantId) ||
    (row.kind === 'payment_link' && row.email);

  const renderHistory = () =>
    (showAllHistory ? history : history.slice(0, HISTORY_PREVIEW)).map(
      (row) => (
        <tr key={row.id} className="event-payments__history-row">
          <td className="table-stack__primary">
            {personName(row)}
            <small className="text-muted d-block">{kindLabel(row)}</small>
          </td>
          <td data-label={p('places')}>{row.places}</td>
          <td data-label={p('amount')}>{money(row.amount, row.currency)}</td>
          <td data-label={s('status')}>
            <Badge color={row.status === 'expired' ? 'warning' : 'secondary'}>
              {p(`holdStatus-${row.status}`)}
            </Badge>
            <small className="text-muted d-block">
              {formatDateTime(
                row.status === 'expired' && row.expiresAt
                  ? row.expiresAt
                  : row.createdAt
              )}
            </small>
          </td>
          <td className="table-stack__actions">
            {canSendAgain(row) && (
              <button
                type="button"
                className="btn btn-outline-primary btn-sm mb-0"
                onClick={() => setAgain(row)}
              >
                <ReplayIcon size={14} />{' '}
                {row.kind === 'offer' ? p('offerAgain') : p('sendNewLink')}
              </button>
            )}
          </td>
        </tr>
      )
    );

  const renderOrphans = () =>
    orphanPayments.map(({ transaction, removed }) => {
      const left = refundableAmount(transaction);
      const refunded = refundedAmountOf(transaction);
      return (
        <tr key={transaction.id}>
          <td className="table-stack__primary">
            {removed ? personName(removed) : transaction.userEmail || '-'}
            <small className="text-muted d-block">
              {removed
                ? p('removedBooking', {
                    date: removed.deletedAt
                      ? formatDateTime(removed.deletedAt)
                      : '-',
                  })
                : transaction.userEmail}
            </small>
          </td>
          <td data-label={p('paid')}>{money(transaction.price)}</td>
          <td data-label={p('alreadyRefunded')}>
            {refunded ? money(refunded) : '-'}
          </td>
          <td data-label={s('date')}>
            {formatDateTime(transaction.createdAt)}
          </td>
          <td className="table-stack__actions">
            <div className="icon-actions">
              <SquareIconButton
                icon={<CashRefundIcon />}
                title={p('refund')}
                tone="danger"
                id={`orphan-refund-${transaction.id}`}
                disabled={!left}
                onClick={() =>
                  onRefund(
                    transaction,
                    removed ? personName(removed) : transaction.userEmail
                  )
                }
              />
            </div>
          </td>
        </tr>
      );
    });

  const showCancelledBanner = !!event?.cancelledAt && refundable.length > 0;
  if (
    !holding.length &&
    !history.length &&
    !orphanPayments.length &&
    !showCancelledBanner
  ) {
    return null;
  }

  return (
    <>
      {overLimitModal}
      <AlertModal
        visible={!!cancelTarget}
        modalType={AlertModalType.CONFIRMATION}
        handleModal={() => setCancelTarget(null)}
        modalAction={cancelHold}
        message={
          cancelTarget
            ? p('cancelHoldConfirm', {
                name: personName(cancelTarget),
                count: cancelTarget.places,
              })
            : ''
        }
      />
      <PaymentOfferModal
        isOpen={!!again}
        title={again?.kind === 'offer' ? p('offerAgain') : p('sendNewLink')}
        description={again ? personName(again) : ''}
        event={event}
        currency={currency}
        places={again?.places || 1}
        defaultPrice={again?.price ?? undefined}
        defaultBookingFee={again?.bookingFee ?? undefined}
        allowDefaultExpiry={again?.kind === 'offer'}
        confirmLabel={
          again?.kind === 'offer' ? p('sendOffer') : p('sendPaymentLink')
        }
        busy={busy}
        onClose={() => setAgain(null)}
        onConfirm={sendAgain}
      />
      <Modal
        isOpen={!!refundAll}
        toggle={() => !refundAll?.running && setRefundAll(null)}
        centered
      >
        <ModalHeader toggle={() => !refundAll?.running && setRefundAll(null)}>
          {p('refundAll')}
        </ModalHeader>
        <ModalBody>
          {refundAll?.done ? (
            <>
              <p>{p('refundAllDone', { count: refundAll.refunded })}</p>
              {refundAll.failures.length > 0 && (
                <>
                  <p className="text-danger mb-1">
                    {p('refundAllFailed', {
                      count: refundAll.failures.length,
                    })}
                  </p>
                  <ul className="event-payments__failures">
                    {refundAll.failures.map((failure) => (
                      <li key={failure.id}>
                        <strong>{failure.name}</strong>: {failure.message}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </>
          ) : (
            <>
              <p>
                {p('refundAllConfirm', {
                  count: refundable.length,
                  total: money(refundableTotal),
                })}
              </p>
              <small className="event-payments__hint">
                {p('refundKeepsBooking')}
              </small>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          {refundAll?.done ? (
            <Button
              color="primary"
              className="mb-0"
              onClick={() => setRefundAll(null)}
            >
              {s('close')}
            </Button>
          ) : (
            <>
              <Button
                color="secondary"
                className="mb-0"
                disabled={refundAll?.running}
                onClick={() => setRefundAll(null)}
              >
                {s('cancel')}
              </Button>
              <Button
                color="danger"
                className="mb-0"
                disabled={refundAll?.running}
                onClick={refundEverything}
              >
                {refundAll?.running ? p('refunding') : p('refundAll')}
              </Button>
            </>
          )}
        </ModalFooter>
      </Modal>

      {showCancelledBanner && (
        <div className="event-payments__banner">
          <span>
            {p('cancelledEventRefunds', {
              count: refundable.length,
              total: money(refundableTotal),
            })}
          </span>
          <Button
            color="danger"
            size="sm"
            className="mb-0"
            onClick={() => setRefundAll({})}
          >
            {p('refundAll')}
          </Button>
        </div>
      )}

      {(holding.length > 0 || history.length > 0) && (
        <FormCard
          className="mt-3"
          title={
            <>
              {p('awaitingPayment')}
              {holding.length > 0 && (
                <span className="form-card__count">{holding.length}</span>
              )}
            </>
          }
          bodyClassName="form-card__body--flush"
        >
          <Table responsive striped className="table--roomy table--stack mb-0">
            <thead>
              <tr>
                <th>{s('name')}</th>
                <th>{p('places')}</th>
                <th>{p('amount')}</th>
                <th>{p('timeLeft')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {holding.length ? (
                renderHolding()
              ) : (
                <tr>
                  <td colSpan={5} className="text-muted text-center">
                    {p('nobodyAwaitingPayment')}
                  </td>
                </tr>
              )}
              {history.length > 0 && (
                <tr className="series-week-row">
                  <td colSpan={5}>{p('earlierHolds')}</td>
                </tr>
              )}
              {renderHistory()}
            </tbody>
          </Table>
          {history.length > HISTORY_PREVIEW && (
            <div className="event-payments__more">
              <button
                type="button"
                className="btn btn-link btn-sm mb-0"
                onClick={() => setShowAllHistory(!showAllHistory)}
              >
                {showAllHistory
                  ? p('showFewer')
                  : p('showAll', { count: history.length })}
              </button>
            </div>
          )}
        </FormCard>
      )}

      {orphanPayments.length > 0 && (
        <FormCard
          className="mt-3"
          title={
            <>
              {p('paymentsWithoutBooking')}
              <span className="form-card__count">{orphanPayments.length}</span>
            </>
          }
          bodyClassName="form-card__body--flush"
        >
          <p className="form-card__intro">{p('paymentsWithoutBookingHint')}</p>
          <Table responsive striped className="table--roomy table--stack mb-0">
            <thead>
              <tr>
                <th>{s('name')}</th>
                <th>{p('paid')}</th>
                <th>{p('alreadyRefunded')}</th>
                <th>{s('date')}</th>
                <th />
              </tr>
            </thead>
            <tbody>{renderOrphans()}</tbody>
          </Table>
        </FormCard>
      )}
    </>
  );
};

EventPaymentsPanel.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.string,
    cancelledAt: PropTypes.string,
  }),
  currency: PropTypes.string,
  pending: PropTypes.arrayOf(PropTypes.shape({})),
  transactions: PropTypes.arrayOf(PropTypes.shape({})),
  participants: PropTypes.arrayOf(PropTypes.shape({})),
  deletedParticipants: PropTypes.arrayOf(PropTypes.shape({})),
  onChanged: PropTypes.func.isRequired,
  onRefund: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
};

EventPaymentsPanel.defaultProps = {
  event: null,
  currency: '',
  pending: [],
  transactions: [],
  participants: [],
  deletedParticipants: [],
};

export default EventPaymentsPanel;
